import { ErrorStreamChange } from '../models/$error.model';
import { Action, createFeatureSelector, createFeature, createActionGroup, emptyProps, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, Dictionary, EntityState } from '@ngrx/entity';

import publicHelpers from 'globals/helpers/public-helpers';
import { LabelMaps, LabelObject, JoiEvent, EventInvitation, EventItem, Venue, JoiDataPage, cleanPages, combineEventAndPages, Label } from '../../../globals/store/events.data';
import { WFWorker, WorkforceGroup } from '../../../globals/store/workforce.data';
import { PublishInfo, ShareOSSInfo } from '../../../globals/store/share.data';
import { accountActions } from './accounts.era';
import { userActions } from './user.era';
import { makeMapFromListOfObjectsAndKey } from 'globals/helpers/helpers';
import { jsonCopyObject } from 'globals/helpers/helpers';

export {  WFWorker, WorkforceGroup, PublishInfo, ShareOSSInfo, LabelMaps, LabelObject, JoiEvent, EventInvitation, EventItem }

export const LOAD_ALL_EVENTS_FLAG = '--all events--';
export const EVENT_ID = "[Event] selected eventId"; 
export const EVENT_GROUP = "[Event] selected event groups";
export const EVENT_UPDATE = "[Event] selected event update";
export const EVENT_UNLOAD = "[Event] selected event unloaded";
export const EVENT_UPDATE_VENUES = "[Event] selected event venues update"
export const EVENTS_UNLOAD = "[Event] list of events unloaded";
export const START_LOADING = '[Event] start loading event entities'
export const CREATE = '[Event] entity create';
export const UPDATE = '[Event] entity update';
export const DELETE = '[Event] entity delete';
// ...for firebase
export const EVENT_LOADED = "[Event] selected event from db";
export const ADD_ONE  = '[Event] entity one added from db';
export const ADD_MANY_EVENTS = '[Event] entity many added from db'
export const MODIFIED = '[Event] entity modified from db';
export const REMOVED  = '[Event] entity removed from db';
export const SUCCESS  = '[Event] end loading';
export const START_LOADING_SINGLE_EVENT = '[Event] start loading single event'

export interface Snapshot {
  id: string;
  name: string;
  timestamp: number;
  columns?: any;
  template?: any;
}

export interface ContainsId {
  [key:string]: boolean
}

export interface EventsState extends EntityState<JoiEvent> {
  event_loading: boolean
  event_loaded: boolean // should only be set to true after all events are loaded
  events_loading: boolean | string
  all_events_loaded: boolean
  events_unloading: boolean
  eventId: string
  event: JoiEvent,
  eventPages: Dictionary<JoiDataPage>,
  programOrder: string[],
  in_groups: string[]
  labelMaps: LabelMaps
}


export const makeEmptyLabelMaps = ():LabelMaps => ({ // using this elsewhere to initialize components before the store
  activity_labels: publicHelpers.makeLabelMap(),
  asset_labels: publicHelpers.makeLabelMap(),
  session_labels: publicHelpers.makeLabelMap(),
  task_labels: publicHelpers.makeLabelMap(),
  program_labels: publicHelpers.makeLabelMap(),
  performer_labels: publicHelpers.makeLabelMap(),
  sponsor_labels: publicHelpers.makeLabelMap(),
  resource_labels: publicHelpers.makeLabelMap(),
  exhibitor_labels: publicHelpers.makeLabelMap(),
  attendee_labels: publicHelpers.makeLabelMap()
})

const createInitialState = (): EventsState => ({
  event_loading: false,
  event_loaded: false,
  events_loading: false,
  all_events_loaded: false,
  events_unloading: false,
  eventId: null,
  event: null,
  eventPages: {},
  in_groups: null,
  ids:[],
  entities: {},
  labelMaps: makeEmptyLabelMaps(),
  programOrder: null
})

export const getEventsState = createFeatureSelector<EventsState>('events');

// Entity adapter
export const adapter = createEntityAdapter<JoiEvent>({
  selectId:(e:JoiEvent)=>e.eventId
});

// Actions
export const eventsActions = createActionGroup({
  source: 'Events',
  events: {
    'Select Event Id': (payload:string) => ({ payload }),
    'Update Event': (changes: Partial<JoiEvent>) => ({ changes }),
    'Update Event Venues': (changes: Venue[]) => ({ changes }),
    'Unload Event': emptyProps(),
    'Unload Events': emptyProps(),
    'Start Loading Single Event': emptyProps(),
    'Start Events Loading': emptyProps(),
    'Create Event Entity': (event: JoiEvent, log_data: Partial<ErrorStreamChange>) => ({ event, log_data }),
    'Update Event Entity': (id: string, changes: Partial<JoiEvent>, log_data: Partial<ErrorStreamChange>) => ({ id, changes, log_data }),
    'Delete Event Entity': (id: string, log_data: Partial<ErrorStreamChange>) => ({ id, log_data }),
    'Load Event': (payload:JoiEvent) => ({ payload }),
    'Add One Event': (event: JoiEvent) => ({ event }),
    'Add Many Events': (payload: JoiEvent[]) => ({ payload }),
    'Modify Event Entity': (id: string, changes: Partial<JoiEvent>) => ({ id, changes }),
    'Remove Event Entity': (id: string) => ({ id }),
    'Event Success': emptyProps(),
  }
});


export const eventsReducer = createReducer<EventsState, Action<string>>(
  {...createInitialState()},
  on(eventsActions.selectEventId, (state, { payload }) => {
    if(state.eventId === payload) return state
    else if(payload === LOAD_ALL_EVENTS_FLAG) {
      return {...state, eventId:LOAD_ALL_EVENTS_FLAG, event:null, event_loaded:false}
    }
    return {...state, eventId:payload, event_loading:true}
  }),
  on(eventsActions.updateEventVenues, (state, { changes }) => {
    const event_venues = state.event.venues ? state.event.venues : {}
    changes.forEach(venue => event_venues[venue.id] = venue)
    const updated_venue_events = {...state.event, venues:event_venues }
    return {...state, event:updated_venue_events}
  }),
  on(eventsActions.unloadEvent, (state) => ({
    ...createInitialState(),
    ids: state.ids,
    entities: state.entities,
    events_loading: state.events_loading,
    all_events_loaded: state.all_events_loaded
  })),
  on(eventsActions.unloadEvents, (state) => ({
    ...state,
    ids: [],
    entities: {},
    events_loading: false,
    all_events_loaded: false,
    events_unloading: true,
  })),
  on(eventsActions.startEventsLoading, (state) => ({
    ...state,
    event:null,
    ids:[],
    entities: {},
    events_loading: true,
    all_events_loaded: false, 
    events_unloading:false
  })),
  on(eventsActions.createEventEntity, (state, { event }) => adapter.addOne(event, {...state, events_loading:true, events_unloading:false})),
  on(eventsActions.updateEventEntity, (state, { id, changes }) => adapter.updateOne({
      id: id,
      changes: changes,
  }, {...state, events_loading:id})),
  on(eventsActions.deleteEventEntity, (state, { id }) => adapter.removeOne(id, {...state, loading:id})),
  on(eventsActions.loadEvent, (state, { payload }) => {
    const { activity_labels: a, session_labels: s, task_labels: t, program_labels: p, performer_labels: q, sponsor_labels: m, exhibitor_labels: e, asset_labels: ass, resource_labels: r, attendee_labels: d } = payload // "m" for money, heh
    const { activity_labels, attendee_labels, session_labels, task_labels, program_labels, performer_labels, sponsor_labels, exhibitor_labels, asset_labels, resource_labels } = state.labelMaps
    const labelMaps: LabelMaps = {
        activity_labels: a ? makeMapFromListOfObjectsAndKey<Label>(a, 'id') : activity_labels,
        attendee_labels: d ? makeMapFromListOfObjectsAndKey<Label>(d, 'id') : attendee_labels,
        asset_labels: ass ? makeMapFromListOfObjectsAndKey<Label>(ass, 'id') : asset_labels,
        session_labels: s ? makeMapFromListOfObjectsAndKey<Label>(s, 'id') : session_labels,
        task_labels: t ? makeMapFromListOfObjectsAndKey<Label>(t, 'id') : task_labels,
        program_labels: p ? makeMapFromListOfObjectsAndKey<Label>(p, 'id') : program_labels,
        performer_labels: q ? makeMapFromListOfObjectsAndKey<Label>(q, 'id') : performer_labels,
        sponsor_labels: m ? makeMapFromListOfObjectsAndKey<Label>(m, 'id') : sponsor_labels,
        exhibitor_labels: e ? makeMapFromListOfObjectsAndKey<Label>(e, 'id') : exhibitor_labels,
        resource_labels: r ? makeMapFromListOfObjectsAndKey<Label>(r, 'id') : resource_labels,
      }
      const loaded_event = {...payload}
      loaded_event.is_readonly = evalEventReadonly(loaded_event)
      // feel free to take byteSize out
      const byteSize = (new Blob([JSON.stringify(loaded_event)])).size
      
      loaded_event.byteSize = byteSize
      return {...state, event:loaded_event, event_loading:false, labelMaps, event_loaded:true, programOrder: payload.programOrder || null }
  }),
  on(eventsActions.addOneEvent, (state, { event }) => adapter.upsertOne(event, {...state, events_unloading:false })),
  on(eventsActions.addManyEvents, (state, { payload }) => {
    const events = jsonCopyObject(payload);
    const many_ids = Object.keys(events);
    many_ids.forEach(id=>{
      events[id].is_readonly = evalEventReadonly(events[id]);
    })
    return adapter.upsertMany(events, {...state, events_unloading:false  })
  }),
  on(eventsActions.modifyEventEntity, (state, { id, changes }) => {
    const changesCopy = jsonCopyObject(changes);
    changesCopy.is_readonly = evalEventReadonly(changes as JoiEvent) ;
    return adapter.updateOne({
      id:id,
      changes: changesCopy
    }, {...state, events_loading:false, events_unloading:false});
  }),
  on(eventsActions.removeEventEntity, (state, { id }) => adapter.removeOne(id, {...state, events_loading:false})),
  on(eventsActions.eventSuccess, (state) => ({ ...state, events_loading: false, all_events_loaded: true, events_unloading:false })),
  on(userActions.logout, (state) => createInitialState()),
  on(accountActions.deleteAccount, (state, { id }) => {
    const eventIds_with_no_account = [...state.ids].filter(eventId=>state.entities[eventId].accountId === id).map(eventId=>eventId as string) 
    return adapter.removeMany(eventIds_with_no_account,state)
  })
);

const evalEventReadonly = (event:JoiEvent) => (!event.can_access || event.closed); // Tony wants suspended events to not show as read only but only suspended

export const eventsFeature = createFeature({
  name: 'events',
  reducer: eventsReducer,
});

export const eventStateSelector = createFeatureSelector<EventsState>('events');
